import type { SetDefinition } from '../data/types';
import type { DeviceId, InputDevice, MenuIntents } from '../input/intents';
import { t } from '../shared/i18n';
import { wrap } from '../shared/scalar';
import { setTempo, stageMinutes } from '../sim/lineup';
import { PLAYER_IDS, type PlayerId } from '../sim/state';
import type { HintPart } from './dom';
import { formatNumber } from './format';
import type { DevicePrompts, KeyStyle } from './prompts';
import type { LobbyModel, LobbySeat, StageCard } from './types';

export const NAME_LENGTH = 12;
const ROOM_LINK = /rejoindre=([^&\s]+)/i;

// A code typed by hand or pasted from the link: spaces and case do not matter.
export function normalizeRoomCode(raw: string): string {
  const linked = ROOM_LINK.exec(raw);
  return (linked?.[1] ?? raw).replace(/\s+/g, '').toUpperCase();
}

export function roomCodeFromHash(hash: string): string {
  return normalizeRoomCode(ROOM_LINK.exec(hash)?.[0] ?? '');
}

export { defaultName } from '../shared/i18n';

export function seatName(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim().slice(0, NAME_LENGTH).trim();
}

export function stepClass(
  classes: readonly { id: string }[],
  current: string,
  delta: number,
): string | null {
  if (classes.length < 2) {
    return null;
  }
  const index = Math.max(
    classes.findIndex((entry) => entry.id === current),
    0,
  );
  const next = (((index + delta) % classes.length) + classes.length) % classes.length;
  return classes[next]?.id ?? null;
}

export type LobbyView = 'local' | 'entry' | 'room';

export function lobbyView(model: LobbyModel): LobbyView {
  if (model.mode === 'local') {
    return 'local';
  }
  return model.code === null ? 'entry' : 'room';
}

export function ownSeat(model: LobbyModel): LobbySeat | null {
  return model.seats.find((seat) => !seat.remote) ?? null;
}

export function seatAt(model: LobbyModel, playerId: PlayerId): LobbySeat | null {
  return model.seats.find((seat) => seat.playerId === playerId) ?? null;
}

// The merged snapshot only says which kind of device spoke: a pad is the first one until the frame
// says more.
export function deviceOf(device: InputDevice): DeviceId | null {
  if (device === 'keyboardMouse') {
    return 'keyboardMouse';
  }
  return device === 'gamepad' ? 'gamepad:0' : null;
}

export function deviceLabel(device: DeviceId | null): string {
  if (device === null) {
    return t('À distance');
  }
  if (device === 'keyboardMouse') {
    return t('Clavier et souris');
  }
  return t('Manette {number}', {
    number: formatNumber(Number(device.slice('gamepad:'.length)) + 1),
  });
}

export function seatTag(seat: LobbySeat, mode: LobbyModel['mode']): string {
  if (mode === 'local') {
    return deviceLabel(seat.device);
  }
  if (seat.remote) {
    return seat.host ? t('Hôte') : t('Invité');
  }
  return seat.host ? t('Toi · hôte') : t('Toi');
}

export function canTakeSeat(model: LobbyModel, device: DeviceId): boolean {
  return (
    model.seats.length < PLAYER_IDS.length && !model.seats.some((seat) => seat.device === device)
  );
}

export interface LobbyHint {
  parts: readonly HintPart[];
  style: KeyStyle;
}

export function lobbyHint(model: LobbyModel, prompts: DevicePrompts): LobbyHint {
  const view = lobbyView(model);
  const tabs =
    model.role === 'host' && prompts.tabs.length > 0 && view !== 'entry'
      ? [{ keys: prompts.tabs, label: t('mode') }]
      : [];
  const choice = {
    keys: prompts.navigateRow,
    label: hasStageChoice(model) ? t('classe, scène') : t('classe'),
  };
  const confirm = { keys: [prompts.confirm], label: t('valider') };
  if (model.mode === 'local') {
    return {
      parts: [
        { keys: [t('Entrée'), 'A'], label: t('rejoindre') },
        ...tabs,
        choice,
        confirm,
        { keys: [t('Échap'), 'B'], label: t('quitter sa place') },
      ],
      style: 'key',
    };
  }
  return {
    parts: [
      { keys: prompts.navigate, label: t('naviguer') },
      ...tabs,
      ...(view === 'entry' ? [] : [choice]),
      confirm,
      { keys: [prompts.back], label: t('quitter') },
    ],
    style: prompts.style,
  };
}

export type Row =
  'create' | 'discord' | 'code' | 'join' | 'copy' | 'name' | 'class' | 'stage' | 'launch' | 'leave';

export function stageCards(sets: readonly SetDefinition[]): StageCard[] {
  return sets.map((set) => ({
    setId: set.id,
    name: set.name,
    style: set.style ?? '',
    bpm: setTempo(set).bpm,
    decor: set.decor,
    minutes: stageMinutes(set),
  }));
}

function hasStageChoice(model: LobbyModel): boolean {
  return (model.stages?.length ?? 0) > 1;
}

export function rowsOf(model: LobbyModel): readonly Row[] {
  const view = lobbyView(model);
  if (view === 'entry') {
    return ['create', 'discord', 'code', 'join', 'leave'];
  }
  const own = ownSeat(model);
  const rows: Row[] = [];
  if (view === 'room' && own?.host === true) {
    rows.push('copy');
  }
  if (own !== null) {
    rows.push('name', 'class');
  }
  if (own?.host === true) {
    if (hasStageChoice(model)) {
      rows.push('stage');
    }
    rows.push('launch');
  }
  rows.push('leave');
  return rows;
}

export function seatRows(seat: LobbySeat, model: LobbyModel): readonly Row[] {
  if (!seat.host) {
    return ['name', 'class'];
  }
  return hasStageChoice(model) ? ['name', 'class', 'stage', 'launch'] : ['name', 'class', 'launch'];
}

export interface RowStep {
  index: number;
  confirmed: boolean;
  back: boolean;
  side: -1 | 0 | 1;
}

export function stepRow(count: number, index: number, edges: MenuIntents): RowStep {
  const side = (edges.right ? 1 : 0) - (edges.left ? 1 : 0);
  const delta = (edges.down ? 1 : 0) - (edges.up ? 1 : 0);
  const next = count <= 0 ? 0 : wrap(index + delta, count);
  return {
    index: next,
    confirmed: edges.confirm,
    back: edges.back,
    side: side > 0 ? 1 : side < 0 ? -1 : 0,
  };
}
